import { AccessControl, AccessCoordinator, CustomerContext } from '../core/auth';
import { AuthService } from '../core/auth/services/auth.service';
import { applyBrandingColors, resolveCustomerColors, ThemeTarget } from '../core/branding';
import store from '../store';
import { InjectionKey } from '../core/injection-keys';
import { Http } from '../core/api/axios';
import {
    accessConfigErrors,
    accessesForRoutes,
    missingPortsForRoutes,
    ROOT_PATH,
    routePath,
    URL_TOKEN_PARAM
} from './route-registry';
import { BrandingColors, LogoSource, RouteAccess, tenant, TenantApi, TenantConfig } from '../tenants';
import { setFavicon } from '../shared/helpers/favicon';

/**
 * Raíz de composición: el único lugar que arma las piezas a partir del tenant activo.
 * 1. Valida el acceso de sus rutas (`RouteAccess` + `session`) y crea el `AccessCoordinator`, que instala los
 *    interceptores de auth en el cliente HTTP compartido.
 * 2. Crea el adaptador de API del tenant sobre ese mismo cliente y valida que tenga los puertos que piden sus rutas.
 * 3. Crea el `CustomerContext` (aseguradora de la ruta activa: header y colores por aseguradora).
 * 4. Expone lo que la app raíz provee por `provide/inject`.
 */
const accessErrors = accessConfigErrors(tenant.routes, tenant.session);
if (accessErrors.length) {
    throw new Error(`[composition-root] Configuración de acceso inválida en "${tenant.id}": ${accessErrors.join('; ')}`);
}

const access = new AccessCoordinator({
    accesses: accessesForRoutes(tenant.routes),
    rootPath: ROOT_PATH,
    tokenParam: URL_TOKEN_PARAM,
    session: tenant.session
        ? {
              loginPath: routePath(tenant.session.loginRoute),
              landingPath: routePath(tenant.session.landingRoute)
          }
        : undefined
});
access.install(Http);

const api: TenantApi = tenant.api(Http);

const missingPorts = missingPortsForRoutes(tenant.routes, api);
if (missingPorts.length) {
    throw new Error(
        `[composition-root] El adaptador de API de "${tenant.id}" no implementa los puertos que necesitan sus rutas: ` +
            missingPorts.join(', ')
    );
}

/**
 * La aseguradora solo importa si el tenant toma el logo de `/me/` o define perfiles por aseguradora (`customers`).
 * Si no (logo estático y sin perfiles), nunca se pide `/me/` y todo queda como siempre.
 */
const customerContext = new CustomerContext({
    enabled: tenant.branding.logo.source === LogoSource.FromMe || !!tenant.customers,
    access,
    fetchMe: () => AuthService.getUserInfo(Http),
    sessionUserInfo: () => store.getters['auth/userInfo'],
    saveSessionUserInfo: userInfo => store.dispatch('auth/setUserInfo', userInfo)
});

export interface AppContext {
    tenant: TenantConfig;
    access: AccessCoordinator;
    customer: CustomerContext;
    api: TenantApi;
}

export const appContext: AppContext = { tenant, access, customer: customerContext, api };

/** Valores que la instancia raíz de Vue provee a todos los componentes. */
export const appProvides: Record<InjectionKey, unknown> = {
    [InjectionKey.Tenant]: tenant,
    [InjectionKey.Access]: access as AccessControl,
    [InjectionKey.Customer]: customerContext,
    [InjectionKey.ClaimsApi]: api.claims,
    [InjectionKey.ItemApi]: api.item,
    [InjectionKey.ClaimManagementApi]: api.claimManagement || null,
    [InjectionKey.ItemClaimsApi]: api.itemClaims || null
};

/**
 * Expone la marca al CSS: colores como custom properties (por defecto los del tenant) y clases de estilo opcionales
 * en `<html>` (`styles.scss`).
 */
export const applyBrandingCssVariables = (
    colors: BrandingColors = tenant.branding.colors,
    root: HTMLElement = document.documentElement
): void => {
    applyBrandingColors(colors, { root });
    root.classList.toggle('tenant-floating-combobox-labels', tenant.branding.floatingComboboxLabels);
};

/**
 * Colores de una aseguradora (`customers[customerId].colors` sobre `branding.colors`; sin customer → los del tenant)
 * en variables CSS y en el tema de Vuetify (`vuetify.framework.theme`, reactivo).
 */
export const applyCustomerBranding = (customerId: number | null, theme?: ThemeTarget | null): void => {
    applyBrandingColors(resolveCustomerColors(tenant.branding.colors, tenant.customers, customerId), { theme });
};

/**
 * Aseguradora con la que arranca el branding, antes de que el router resuelva la primera navegación: la de la sesión
 * guardada si la ruta inicial es de sesión o pública; en una ruta por token, ninguna (colores del tenant hasta que
 * responde `/me/` con el token). Evita el parpadeo de color al recargar.
 */
export const initialCustomerId = (initialAccess: RouteAccess): number | null => {
    if (initialAccess === RouteAccess.UrlToken) {
        return null;
    }
    const customer = customerContext.sessionCustomer;
    return customer ? customer.customer : null;
};

/** Favicon propio del tenant (`branding.favicon`); sin definir, queda el de `public/index.html` (`favicon.png`). */
export const applyBrandingFavicon = (doc: Document = document): void => {
    if (tenant.branding.favicon) {
        setFavicon(tenant.branding.favicon, doc);
    }
};
